/**
 * The start values form of a conversation (agion/Agion#2413): what it holds,
 * which values it marks, and what a launch sends.
 *
 * The form opens with the values this person entered for the previous run in
 * this conversation (launchValuesStore), else the mission's defaults. A launch
 * takes the values, typed by their declared type; while a required one is
 * empty or a value is not of its type nothing goes out and the form marks it
 * with the reason. A started run keeps what was entered for the next run. A
 * launch the platform refused for missing start values marks the values the
 * refusal lists in missing_inputs.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PendingFile } from './useChatFileUpload'
import type { LaunchInput } from '../types/mission'
import { initialLaunchValues, prepareLaunchValues, refusedStartValues, isFileLaunchInput, type ValueProblem } from '../utils/launchInputs'
import { loadLaunchValues, saveLaunchValues } from '../utils/launchValuesStore'

const NONE: LaunchInput[] = []

interface UseLaunchInputsOptions {
  userId?: string
  missionId: string
  /** The mission's start values (Mission.launchInputs); absent asks for nothing. */
  inputs?: LaunchInput[]
  canStart?: boolean
  files?: PendingFile[]
  addFiles?: (files: File[]) => string[]
  retryFile?: (id: string) => void
  removeFile?: (id: string) => void
}

export interface LaunchInputsForm {
  inputs: LaunchInput[]
  canStart?: boolean
  values: Record<string, string>
  files?: PendingFile[]
  uploadFile?: (name: string, file: File) => void
  retryFile?: (id: string) => void
  removeFile?: (id: string) => void
  clearFileValues?: () => void
  /** Values the form marks, with why: empty required ones, text not of its
   *  type, or values a refusal listed. */
  marked: Record<string, ValueProblem>
  /** The mission has a required start value, so a run never starts blind. */
  hasRequired: boolean
  setValue: (name: string, value: string) => void
  /** The values to send, or null when one cannot be sent (then marked). */
  take: () => Record<string, unknown> | null
  /** Keeps what was entered for the next run in this conversation. */
  remember: () => void
  /** Marks the values a missing start values refusal names; false for any other error. */
  refused: (err: unknown) => boolean
}

function opening(inputs: LaunchInput[], userId: string | undefined, missionId: string): Record<string, string> {
  return initialLaunchValues(inputs, userId ? loadLaunchValues(userId, missionId) : null)
}

export function useLaunchInputs({ userId, missionId, inputs = NONE, canStart, files = [], addFiles, retryFile, removeFile }: UseLaunchInputsOptions): LaunchInputsForm {
  const [values, setValues] = useState<Record<string, string>>(() => opening(inputs, userId, missionId))
  const [marked, setMarked] = useState<Record<string, ValueProblem>>({})
  // The launch handler reads the values when it runs, not when it was made.
  const valuesRef = useRef(values)
  valuesRef.current = values
  const inputsRef = useRef(inputs)
  inputsRef.current = inputs

  const canStartRef = useRef(canStart)
  canStartRef.current = canStart

  const filesRef = useRef(files)
  filesRef.current = files

  // Another conversation, person or set of start values opens the form afresh.
  const signature = inputs.map((i) => `${i.name}\u0000${i.type}\u0000${i.format ?? ''}\u0000${i.defaultValue ?? ''}`).join('\u0001')
  const openedFor = useRef(`${userId ?? ''}|${missionId}|${signature}`)
  useEffect(() => {
    const key = `${userId ?? ''}|${missionId}|${signature}`
    if (openedFor.current === key) return
    openedFor.current = key
    const next = opening(inputsRef.current, userId, missionId)
    valuesRef.current = next
    setValues(next)
    setMarked({})
  }, [userId, missionId, signature])

  const setValue = useCallback((name: string, value: string) => {
    if (value !== '' && inputsRef.current.some((input) => input.name === name && isFileLaunchInput(input)) && canStartRef.current !== true) return
    setValues((current) => {
      const next = { ...current, [name]: value }
      valuesRef.current = next
      return next
    })
    // An edited value is judged again at the next launch.
    setMarked((current) => {
      if (!(name in current)) return current
      const { [name]: _edited, ...rest } = current
      return rest
    })
  }, [])

  const take = useCallback((): Record<string, unknown> | null => {
    const entered = { ...valuesRef.current }
    const fileProblems: Record<string, ValueProblem> = {}
    for (const input of inputsRef.current) {
      if (!isFileLaunchInput(input)) continue
      const selection = entered[input.name]
      if (!selection) continue
      const file = filesRef.current.find((file) => file.id === selection)
      if (file?.status === 'complete' && file.hostFileId) entered[input.name] = file.hostFileId
      else { entered[input.name] = ''; fileProblems[input.name] = 'file' }
    }
    const { values: sent, problems } = prepareLaunchValues(inputsRef.current, entered)
    Object.assign(problems, fileProblems)
    setMarked(problems)
    return Object.keys(problems).length > 0 ? null : sent
  }, [])

  const remember = useCallback(() => {
    if (!userId || inputsRef.current.length === 0) return
    const entered: Record<string, string> = {}
    for (const input of inputsRef.current) {
      if (!isFileLaunchInput(input)) entered[input.name] = valuesRef.current[input.name] ?? ''
    }
    saveLaunchValues(userId, missionId, entered)
  }, [userId, missionId])

  const refused = useCallback((err: unknown): boolean => {
    const named = refusedStartValues(err, inputsRef.current)
    if (named === null) return false
    // An older backend lists none: the form stays open with its message, unmarked.
    setMarked(Object.fromEntries(named.map((name) => [name, 'missing' as const])))
    return true
  }, [])

  const uploadFile = useCallback((name: string, file: File) => {
    if (canStartRef.current !== true) return
    const id = addFiles?.([file])?.[0]
    if (id) setValue(name, id)
  }, [addFiles, setValue])

  const retrySelectedFile = useCallback((id: string) => {
    if (canStartRef.current === true) retryFile?.(id)
  }, [retryFile])

  const clearFileValues = useCallback(() => {
    setValues((current) => {
      const next = { ...current }
      for (const input of inputsRef.current) if (isFileLaunchInput(input)) next[input.name] = ''
      valuesRef.current = next
      return next
    })
  }, [])

  const hasRequired = useMemo(() => inputs.some((i) => i.required), [inputs])

  return { inputs, canStart, values, marked, hasRequired, setValue, take, remember, refused, files, uploadFile, retryFile: retrySelectedFile, removeFile, clearFileValues }
}
